import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTenantConfigs, TenantConfig, DEFAULT_TENANT } from './tenantConfig';

/**
 * Tenant Context
 *
 * Provides tenant information and configuration throughout the application.
 * Detects tenant from URL path and loads appropriate configuration.
 */

interface TenantContextType {
  tenant: string;
  config: TenantConfig;
  isLoading: boolean;
  setTenant: (tenant: string) => void;
  hasFeature: (feature: string) => boolean;
  getThemeColor: (key: string) => string;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const { tenant: urlTenant } = useParams<{ tenant: string }>();
  const navigate = useNavigate();
  const [tenant, setTenantState] = useState<string>(urlTenant || DEFAULT_TENANT);
  const [config, setConfig] = useState<TenantConfig>(() => getTenantConfigs()[DEFAULT_TENANT]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadTenant = async () => {
      setIsLoading(true);

      const tenantId = urlTenant || DEFAULT_TENANT;

      // Check if tenant exists
      const tenantConfigs = getTenantConfigs();
      if (!tenantConfigs[tenantId]) {
        console.warn(`Tenant '${tenantId}' not found, using default`);
        setTenantState(DEFAULT_TENANT);
        setConfig(tenantConfigs[DEFAULT_TENANT]);
      } else {
        setTenantState(tenantId);
        setConfig(tenantConfigs[tenantId]);
      }

      setIsLoading(false);
    };

    loadTenant();
  }, [urlTenant]);

  const setTenant = (newTenant: string) => {
    const tenantConfigs = getTenantConfigs();
    if (tenantConfigs[newTenant]) {
      setTenantState(newTenant);
      setConfig(tenantConfigs[newTenant]);
      navigate(`/${newTenant}`);
    }
  };

  const hasFeature = (feature: string): boolean => {
    return config.features?.[feature] ?? false;
  };

  const getThemeColor = (key: string): string => {
    return (config.theme as unknown as Record<string, string>)?.[key] ?? '';
  };

  return (
    <TenantContext.Provider
      value={{
        tenant,
        config,
        isLoading,
        setTenant,
        hasFeature,
        getThemeColor,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export default TenantContext;
